import React, { Fragment } from 'react'
import Grid from '@mui/material/Grid'
import { Formik } from 'formik'
import { compact } from 'lodash'

import { ICanvasStep } from 'pages/orchestration/workflows/edit/interfaces'
import { formatCodeFromName } from 'utils/formatters'
import { useWorkflows } from 'api/workflows/queries/useWorkflows'
import { SidebarConfigsWrapper } from 'pages/orchestration/workflows/edit/components/sidebarConfigs/components/SidebarConfigsWrapper'
import { SidebarConfigsHeader } from 'pages/orchestration/workflows/edit/components/sidebarConfigs/components/SidebarConfigsHeader'
import { SidebarConfigsBody } from 'pages/orchestration/workflows/edit/components/sidebarConfigs/components/SidebarConfigsBody'

import { getGenericChildWorkflowInitialValues } from 'pages/orchestration/workflows/edit/utils/forms/activities/genericChildWorkflow/genericChildWorkflowInitialValues'
import { getWorkflowFormValidationSchema } from 'pages/orchestration/workflows/edit/utils/forms/activities/genericChildWorkflow/genericChildWorkflowValidation'
import { Box, Typography } from '@mui/material'
import { FormInput } from 'components/form/FormInput'
import { FormDroplistButton } from 'components/form/FormDroplistButton'

interface IChildWorkflowFormProps {
  workflowCode: string
  step: ICanvasStep
  steps: Array<ICanvasStep>
  handleClose: () => void
  handleSave: (params: { step: ICanvasStep }) => void
  handleRemove: (params: { step: ICanvasStep }) => void
}

export const ChildWorkflowForm: React.FC<IChildWorkflowFormProps> = props => {
  const { workflowCode, step, steps, handleClose, handleRemove, handleSave } = props
  const usedCodes = compact(steps.map(({ code }) => code).filter(code => code !== step.code))

  const { data: workflows, isLoading } = useWorkflows()
  const workflowsOptions = workflows
    .filter(({ code }) => code && code !== workflowCode)
    .map(({ code, name }) => ({ id: code, name }))

  const onSubmit = (values: any) => {
    handleSave({
      step: {
        ...step,
        valid: true,
        code: values.code,
        description: values.description,
        workflow: {
          ...step.workflow,
          params: {
            workflow: values.workflow,
          },
        } as ICanvasStep['workflow'],
      },
    })
  }

  return (
    <SidebarConfigsWrapper isLoading={isLoading} handleClose={handleClose}>
      <Formik
        initialValues={getGenericChildWorkflowInitialValues({ step })}
        validationSchema={getWorkflowFormValidationSchema({ usedCodes })}
        validateOnMount
        onSubmit={onSubmit}
      >
        {form => {
          const { isValid, submitForm, setFieldValue } = form

          return (
            <Fragment>
              <SidebarConfigsHeader
                isValid={isValid}
                step={step}
                steps={steps}
                handleSave={() => submitForm()}
                handleRemove={() => handleRemove({ step })}
                handleClose={handleClose}
              />

              <SidebarConfigsBody sx={{ px: 3 }}>
                <Box mb={5}>
                  <Box marginBottom={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormInput
                          required
                          formPropertyName="description"
                          fullWidth
                          label="Description Label"
                          placeholder="Enter description"
                          onChange={e => setFieldValue('code', formatCodeFromName(e.target.value))}
                          shouldUpdateDebounced
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Grid container spacing={1}>
                    <Grid item xs={8}>
                      <FormInput
                        formPropertyName="code"
                        fullWidth
                        label="Code"
                        placeholder="Enter code"
                        required
                        shouldUpdateDebounced
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Box marginBottom={3}>
                    <Typography variant="h3">Workflow Details</Typography>
                  </Box>

                  <Box marginBottom={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormDroplistButton
                          disableClearable
                          required
                          fullWidth
                          formPropertyName="workflow"
                          label="Workflow"
                          placeholder="Select Workflow"
                          items={workflowsOptions}
                          valueKey="id"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </SidebarConfigsBody>
            </Fragment>
          )
        }}
      </Formik>
    </SidebarConfigsWrapper>
  )
}
